import { useCallback } from 'react';
import { message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import type { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import { useStatsigClient } from '@statsig/react-bindings';
import { useToken, toStatsigUser } from '@/common/helper';
import { useWebSocket } from '@/context/WebSocketContext';
import { fetchMenu } from '@/redux-toolkit/slice/menuSlice';
import { setAuthState } from '@/redux-toolkit/slice/authSlice';
import {
  clearAllStoreState,
  persistor,
  resetDashboardFilterStore,
} from '@/redux-toolkit/store';
import { queryClient } from '@/queryClient';
import { MenuItemData } from '@/apis/types';
import { fetchTeamsDDL } from '@/redux-toolkit/slice/teamsSlice';
import { abortAllPendingRequests } from '@/common/interceptor/sessionAbortController';
import { sendPendingActivityNow } from '@/hooks/useActivityBeacon/useActivityBeacon';

export interface ApplyAuthSessionParams {
  access: string;
  refresh?: string;
  successMessage?: string;
  defaultRoute?: string;
  syncStatsig?: boolean;
  /**
   * Explicit route to land on after the session is applied, taking priority
   * over the computed first-menu-route. Used by Exit Preview to return the
   * admin to the settings screen the preview was launched from (Users List
   * or Roles & Permissions), regardless of where they navigated while
   * previewing.
   */
  returnTo?: string;
  /**
   * When true, skip the default "navigate to first menu route" behavior and
   * leave the admin on whatever route they're currently on. Used by
   * mid-preview role/team/user switches, where the client requires staying
   * put rather than bouncing to the default route. `returnTo` still always
   * wins over this. Safe because PrivateRoutes re-checks the current route
   * against the freshly-fetched menu on every render and redirects on its
   * own if the new role/team no longer permits it.
   */
  preserveRoute?: boolean;
  /**
   * Which kind of session transition this is:
   * - `'login'` (default): real login/OAuth/refresh identity switch. Tokens
   *   are written to localStorage (shared across tabs) and the persisted
   *   Redux store is fully purged, as before.
   * - `'enterPreview'`: "View As User" / role preview. Tokens are written to
   *   this tab's sessionStorage only; the admin's real localStorage session
   *   and every other open tab are left untouched, and the persisted store
   *   is not purged (redux-persist writes route to sessionStorage for the
   *   duration of the preview -- see `createReduxPersistStorage`).
   * - `'exitPreview'`: ends the tab-local preview session and restores the
   *   admin's real session (already intact in localStorage) for this tab.
   */
  mode?: 'login' | 'enterPreview' | 'exitPreview';
}

export interface UseSessionTransitionReturn {
  applyAuthSession: (params: ApplyAuthSessionParams) => Promise<boolean>;
  clearStoredAuth: () => Promise<void>;
}

/**
 * Reusable hook to handle session transitions (login, OAuth callbacks, impersonation / view-as-user).
 * Centralizes token storage, cache clearing, menu fetching, zero-permission teardown, and dynamic route navigation.
 */
export const useSessionTransition = (): UseSessionTransitionReturn => {
  const {
    saveLoginUser,
    removeToken,
    setUserId,
    decodedUserToken,
    savePreviewSession,
    endPreviewSession,
  } = useToken();
  const webSocketContext = useWebSocket();
  const webSocketService = webSocketContext?.webSocketService;
  const setSelectedUserID = webSocketContext?.setSelectedUserID;

  const navigate = useNavigate();
  const dispatch =
    useDispatch<ThunkDispatch<unknown, unknown, UnknownAction>>();
  const statsigContext = useStatsigClient();

  const clearStoredAuth = useCallback(async () => {
    sendPendingActivityNow();
    abortAllPendingRequests();
    removeToken();
    setUserId(null);
    setSelectedUserID?.(null);
    webSocketService?.disconnect?.();
    await clearAllStoreState();
    localStorage.removeItem('@refreshToken');
  }, [removeToken, setUserId, setSelectedUserID, webSocketService]);

  const applyAuthSession = useCallback(
    async ({
      access,
      refresh,
      successMessage,
      syncStatsig = true,
      returnTo,
      preserveRoute = false,
      mode = 'login',
    }: ApplyAuthSessionParams): Promise<boolean> => {
      if (!access) {
        console.error('applyAuthSession called without an access token');
        return false;
      }

      const isPreviewTransition =
        mode === 'enterPreview' || mode === 'exitPreview';

      try {
        // 0. Send the previous session's pending activity events with its
        // own token (before step 1 swaps tokens), then cancel all in-flight
        // requests from the previous session
        sendPendingActivityNow();
        abortAllPendingRequests();

        // 1. Save access and refresh tokens to the correct storage for this
        // transition: sessionStorage (tab-scoped) while entering/exiting
        // preview, localStorage (shared) for a real login.
        if (mode === 'enterPreview') {
          savePreviewSession(access, refresh);
        } else if (mode === 'exitPreview') {
          endPreviewSession();
          await saveLoginUser(access);
          if (refresh) {
            localStorage.setItem('@refreshToken', refresh);
          }
        } else {
          await saveLoginUser(access);
          if (refresh) {
            localStorage.setItem('@refreshToken', refresh);
          }
        }

        // 2. Clear previous Redux store (in-memory) and React Query cache.
        // Preview transitions never purge the persisted store -- it is not
        // touched by preview sessions in the first place (see
        // createReduxPersistStorage), so purging here would only risk
        // wiping the admin's real, cross-tab persisted UI state.
        await clearAllStoreState({ purgePersisted: !isPreviewTransition });
        queryClient.clear();

        // 3. Mark auth state as authenticated
        dispatch(setAuthState(true));
        dispatch(fetchTeamsDDL(access || ''));

        // 4. Update Statsig user context if enabled
        const decoded = decodedUserToken();
        if (syncStatsig) {
          try {
            if (decoded && statsigContext?.client?.updateUserAsync) {
              statsigContext.client
                .updateUserAsync(toStatsigUser(decoded))
                .catch(() => {
                  // Best effort
                });
            }
          } catch {
            // Best effort
          }
        }

        // 5. Fetch dynamic user menu permissions
        const menuResult = await dispatch(fetchMenu(access)).unwrap();
        const menuItems: MenuItemData[] = Array.isArray(menuResult)
          ? menuResult
          : (menuResult as { items?: MenuItemData[] })?.items || [];

        // 6. Zero-permission guard
        if (!menuItems || menuItems.length === 0) {
          message.error(
            "You don't have permission to access this application. Please contact your administrator."
          );
          setUserId(null);
          setSelectedUserID?.(null);
          webSocketService?.disconnect?.();
          if (isPreviewTransition) {
            // Only tear down this tab's preview overlay -- never the
            // admin's real localStorage session or other open tabs.
            endPreviewSession();
          } else {
            removeToken();
            await persistor.flush();
            await persistor.purge();
            resetDashboardFilterStore();
            localStorage.clear();
          }
          return false;
        }
        let isOnboarded = false;
        const user = decoded?.role?.toLowerCase() === 'admin';

        if (decoded) {
          isOnboarded = decoded?.is_onboarded === true;
        }

        if (!isOnboarded && user) {
          navigate('/onboarding');
          return;
        }
        if (returnTo) {
          navigate(returnTo);
        } else if (
          !preserveRoute &&
          menuItems &&
          Array.isArray(menuItems) &&
          menuItems.length > 0
        ) {
          // const firstRoute = menuItems[1];
          const firstRoute = menuItems.find(
            (item: MenuItemData) => item.route && typeof item.route === 'string'
          );
          if (firstRoute) {
            navigate(`/${firstRoute.route}`);
          }
        }
        // preserveRoute + no returnTo: stay put. PrivateRoutes re-checks the
        // current route against the menu we just fetched on its next render
        // and redirects on its own if the new role/team no longer permits it.

        // 8. Display success toast if message is provided
        if (successMessage) {
          message.success(successMessage);
        }

        return true;
      } catch {
        message.error('Failed to initialize user session permissions.');
        return false;
      }
    },
    [
      saveLoginUser,
      savePreviewSession,
      endPreviewSession,
      decodedUserToken,
      dispatch,
      navigate,
      removeToken,
      setSelectedUserID,
      setUserId,
      statsigContext?.client,
      webSocketService,
    ]
  );

  return {
    applyAuthSession,
    clearStoredAuth,
  };
};

export default useSessionTransition;
